import React, { useEffect, useRef, useState } from "react";
import { Send, Loader2, Sparkles, Zap, RotateCcw, Copy, Check, AlertTriangle } from "lucide-react";
import { csuiteApi } from "../../../lib/csuiteApi";
import { useCSuiteAuth } from "../../../state/CSuiteAuthContext";
import { useLiveUpdates } from "../../../lib/useLiveUpdates";
import { trackEvent } from "../../../lib/analytics";

const QUICK_ACTIONS = [
  { id: "board-update", label: "Draft board update",     prompt: "Draft a board update for this quarter using the standard 4-section brief (Highlights · Risks · Decisions Needed · Next-quarter focus)." },
  { id: "risk-summary", label: "Summarize risks",        prompt: "Give me a 3-bullet summary of the highest-priority portfolio risks and what I should do about each in the next 14 days." },
  { id: "compare",      label: "Compare S/4HANA vs D365",prompt: "Compare our SAP S/4HANA delivery health with our Microsoft Dynamics 365 adoption. Which programme deserves the sponsor's attention this month, and why?" },
  { id: "value",        label: "Value realisation",      prompt: "How are we tracking against planned benefits this quarter? Which initiatives are at risk and what's the recommended intervention?" },
];

/**
 * Capittance NAVBOT — Claude Sonnet 4.5 chat. Executive AI assistant, gated
 * behind an active C-Suite subscription (Starter → chat; Platinum → chat +
 * proactive quick-actions).
 * Session id is stable per browser (localStorage) so the bot has short-term
 * memory across page loads. Backend rehydrates prior turns from Mongo.
 */
export default function AICopilot() {
  const { user } = useCSuiteAuth();
  const tier = (user?.subscription?.plan) || (user?.role === "ADMIN" ? "pro" : "starter");
  const [sessionId] = useState(() => {
    try {
      const k = "capittance_ai_session";
      const cached = localStorage.getItem(k);
      if (cached) return cached;
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      localStorage.setItem(k, id);
      return id;
    } catch { return `session-${Date.now()}`; }
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    csuiteApi.aiHistory(sessionId).then(r => setMessages(r.messages || [])).catch(() => {});
  }, [sessionId]);

  // Refetch history whenever a NAVBOT proactive alert arrives on the WS bus.
  useLiveUpdates((ev) => {
    if (ev?.type === "navbot.alert") {
      csuiteApi.aiHistory(sessionId).then(r => setMessages(r.messages || [])).catch(() => {});
    }
  });

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = async (text, action) => {
    if (!text.trim() || busy) return;
    setErr("");
    const nowIso = new Date().toISOString();
    setMessages(m => [...m, { role: "user", content: text, ts: nowIso }]);
    setInput("");
    setBusy(true);
    try {
      const r = await csuiteApi.aiChat(sessionId, text, action);
      setMessages(m => [...m, { role: "assistant", content: r.reply, ts: new Date().toISOString() }]);
      trackEvent("navbot_demo_used", { action: action || "free_prompt" });
    } catch (e) {
      setErr(e.message || "AI service error");
    } finally {
      setBusy(false);
    }
  };

  const resetSession = () => {
    try { localStorage.removeItem("capittance_ai_session"); } catch { /* noop */ }
    setMessages([]);
    window.location.reload();
  };

  const copy = async (id, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch { /* noop */ }
  };

  return (
    <div className="grid md:grid-cols-[280px_1fr] gap-4" data-testid="ai-copilot">
      {/* Left rail — persona + quick actions */}
      <aside className="card p-4 h-fit">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-full grid place-items-center text-white" style={{ background: "linear-gradient(135deg, #7A1F2B 0%, #B03A48 100%)" }}>
            <Sparkles size={16} />
          </div>
          <div>
            <div className="font-display text-sm font-extrabold">NAVBOT</div>
            <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Executive AI · Claude 4.5</div>
          </div>
        </div>
        <div className="text-xs text-zinc-600 leading-relaxed mb-3">
          Your always-on executive co-pilot. Ask about S/4HANA, D365, risks, value realisation, or request a full board update — NAVBOT answers in boardroom-ready language grounded in <b>your</b> live portfolio only.
        </div>
        <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-2">Quick actions</div>
        <div className="space-y-1.5">
          {QUICK_ACTIONS.map(a => (
            <button key={a.id}
                    className="w-full text-left text-xs px-2.5 py-2 rounded-sm border border-[var(--border)] hover:border-[#B03A48] hover:bg-[#FDF2F4] transition-colors"
                    onClick={() => send(a.prompt, a.id)}
                    disabled={busy}
                    data-testid={`copilot-action-${a.id}`}>
              <span className="inline-flex items-center gap-1.5"><Zap size={11} style={{ color: "#7A1F2B" }}/>{a.label}</span>
            </button>
          ))}
        </div>
        <button onClick={resetSession} className="mt-3 w-full text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-800 flex items-center gap-1"
                data-testid="copilot-reset">
          <RotateCcw size={10}/> Reset conversation
        </button>
        <div className="mt-3 pt-3 border-t border-[var(--border)] font-mono text-[9px] uppercase tracking-widest text-zinc-500">
          Session · {sessionId.slice(-8)}<br/>
          Tier · <span className={tier === "pro" ? "text-emerald-700" : ""}>{tier}</span>
        </div>
      </aside>

      {/* Chat window */}
      <div className="card p-0 flex flex-col" style={{ minHeight: 560 }}>
        <div className="px-4 py-3 border-b border-[var(--border)] flex items-center gap-2 flex-wrap">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
            <Sparkles size={12}/> NAVBOT
            <span className={`pill text-[10px] ${tier === "pro" ? "pill-green" : "pill-neutral"}`}>{tier === "pro" ? "Platinum" : "Starter"}</span>
          </div>
          <div className="ml-auto text-xs text-zinc-400">{messages.length} message{messages.length === 1 ? "" : "s"}</div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.length === 0 && !busy && (
            <div className="text-center py-10 text-zinc-500">
              <Sparkles size={28} className="mx-auto mb-2" style={{ color: "#B03A48" }}/>
              <div className="font-display text-lg font-bold text-zinc-800">Ask me anything</div>
              <div className="text-sm mt-1">Try a quick action on the left or type below.</div>
            </div>
          )}
          {messages.map((m, i) => (
            <MsgBubble key={i} m={m} idx={i} copiedId={copiedId} onCopy={copy} />
          ))}
          {busy && (
            <div className="flex items-center gap-2 text-sm text-zinc-500 pl-2" data-testid="copilot-thinking">
              <Loader2 size={14} className="animate-spin"/> Thinking…
            </div>
          )}
          {err && <div className="text-sm text-red-700 pl-2">{err}</div>}
        </div>

        <div className="border-t border-[var(--border)] p-3 flex items-end gap-2">
          <textarea
            className="fld flex-1 text-sm resize-none py-2 px-3"
            rows={2}
            placeholder="Ask about your portfolio…"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send(input);
              }
            }}
            disabled={busy}
            data-testid="copilot-input"
          />
          <button className="btn btn-primary" onClick={() => send(input)} disabled={busy || !input.trim()} data-testid="copilot-send" title="Send · Enter">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
          </button>
        </div>
      </div>
    </div>
  );
}

function MsgBubble({ m, idx, copiedId, onCopy }) {
  const isUser = m.role === "user";
  const isAlert = !isUser && m.meta && m.meta.kind === "red-alert";
  return (
    <div
      className={`flex gap-2 ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={`copilot-msg-${idx}`}
      data-alert={isAlert ? "red" : undefined}
    >
      {!isUser && (
        <div
          className="w-7 h-7 rounded-full grid place-items-center text-white shrink-0"
          style={{ background: isAlert
            ? "linear-gradient(135deg, #B91C1C 0%, #DC2626 100%)"
            : "linear-gradient(135deg, #7A1F2B 0%, #B03A48 100%)" }}
        >
          {isAlert ? <AlertTriangle size={13}/> : <Sparkles size={13}/>}
        </div>
      )}
      <div className={`max-w-[80%] rounded-sm px-3.5 py-2.5 relative group ${
        isUser ? "text-white" : (isAlert ? "bg-red-50 border-l-4 border-red-600 border border-red-200" : "bg-zinc-50 border border-[var(--border)]")
      }`} style={isUser ? { background: "#7A1F2B" } : {}}>
        {isAlert && (
          <div className="font-mono text-[9px] uppercase tracking-widest text-red-700 mb-1">
            Proactive red-signal alert
          </div>
        )}
        <div className="text-sm leading-relaxed whitespace-pre-wrap">{m.content}</div>
        {!isUser && (
          <button onClick={() => onCopy(idx, m.content)}
                  className="absolute -bottom-2 right-2 text-zinc-400 hover:text-zinc-700 bg-white border border-[var(--border)] rounded-sm p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Copy">
            {copiedId === idx ? <Check size={11}/> : <Copy size={11}/>}
          </button>
        )}
      </div>
    </div>
  );
}
